import type { RouteMeta } from 'vue-router'
import type { UserInfo } from '@/types/api'

export type RoleCode = string
export type PermissionCode = string

export const SUPER_ADMIN_ROLES = ['ADMIN', 'SUPER_ADMIN', 'LISTING_ADMIN'] as const

export const ROLE_GROUPS = {
  listingReview: [
    'LISTING_REVIEWER',
    'INITIAL_REVIEWER',
    'LISTING_INITIAL_REVIEWER',
    'RISK_REVIEWER',
    'LISTING_RISK_REVIEWER',
    'RISK_MANAGER',
    'DELIVERY_REVIEWER',
    'LISTING_DELIVERY_REVIEWER',
    'FINAL_REVIEWER',
    'LISTING_FINAL_REVIEWER',
  ],
  listingOperation: [
    'LISTING_OPERATOR',
    'OPERATOR',
    'ANNOUNCEMENT_OPERATOR',
  ],
  dualControlManage: [
    'LISTING_OPERATOR',
    'OPERATOR',
    'LISTING_REVIEWER',
    'INITIAL_REVIEWER',
    'LISTING_INITIAL_REVIEWER',
    'RISK_REVIEWER',
    'LISTING_RISK_REVIEWER',
    'RISK_MANAGER',
    'DELIVERY_REVIEWER',
    'LISTING_DELIVERY_REVIEWER',
    'FINAL_REVIEWER',
    'LISTING_FINAL_REVIEWER',
    'WAREHOUSE_REVIEWER',
    'APPROVAL_MANAGER',
  ],
  inventoryReview: [
    'LISTING_REVIEWER',
    'DELIVERY_REVIEWER',
    'LISTING_DELIVERY_REVIEWER',
    'WAREHOUSE_REVIEWER',
  ],
  issuerReview: [
    'LISTING_REVIEWER',
    'ISSUER_REVIEWER',
  ],
  fundOperation: [
    'OPERATOR',
    'CLEARING',
    'FUND_OPERATOR',
    'APPROVAL_MANAGER',
  ],
  tradeRepair: [
    'OPERATOR',
    'CLEARING_OPERATOR',
    'CLEARING_MANAGER',
    'RISK_REVIEWER',
    'RISK_MANAGER',
    'APPROVAL_MANAGER',
  ],
  opsAlert: [
    'OPERATOR',
    'CLEARING_OPERATOR',
    'CLEARING_MANAGER',
    'RISK_MANAGER',
    'APPROVAL_MANAGER',
  ],
} as const

export const ROUTE_ROLES = {
  listingApplications: [
    ...ROLE_GROUPS.listingReview,
    ...ROLE_GROUPS.listingOperation,
  ],
  listingLifecycle: [
    ...ROLE_GROUPS.listingOperation,
    'RISK_REVIEWER',
    'RISK_MANAGER',
  ],
  dualControlCenter: ROLE_GROUPS.dualControlManage,
  inventoryAdmin: ROLE_GROUPS.inventoryReview,
  issuerProfiles: ROLE_GROUPS.issuerReview,
  fundOperation: ROLE_GROUPS.fundOperation,
  tradeRepair: ROLE_GROUPS.tradeRepair,
  opsAlert: ROLE_GROUPS.opsAlert,
} as const

export const DUAL_CONTROL_ACTION_ROLES = {
  reject: ROLE_GROUPS.dualControlManage,
  withdraw: ROLE_GROUPS.dualControlManage,
  expire: ROLE_GROUPS.dualControlManage,
} as const

export interface AccessOptions {
  fallbackAllow?: boolean
}

export type RouteAccessTarget = RouteMeta | {
  meta?: RouteMeta
  matched?: Array<{ meta: RouteMeta }>
}

const normalizeCodes = (value: unknown): string[] => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value.flatMap(item => normalizeCodes(item))
  }
  if (typeof value === 'string') {
    return value
      .split(/[,;|\s]+/)
      .map(item => item.trim())
      .filter(Boolean)
  }
  return []
}

const unique = (codes: string[]) => Array.from(new Set(codes))

const getRouteMetas = (target: RouteAccessTarget): RouteMeta[] => {
  const maybeRoute = target as { meta?: RouteMeta; matched?: Array<{ meta: RouteMeta }> }
  if (Array.isArray(maybeRoute.matched)) {
    return maybeRoute.matched.map(record => record.meta)
  }
  if (maybeRoute.meta) {
    return [maybeRoute.meta]
  }
  return [target as RouteMeta]
}

export const getUserRoles = (userInfo: UserInfo | null | undefined): RoleCode[] => {
  const role = (userInfo as (UserInfo & { role?: string | string[] }) | null | undefined)?.role
  return unique([
    ...normalizeCodes(userInfo?.roles),
    ...normalizeCodes(role),
  ])
}

export const getUserPermissions = (
  userInfo: UserInfo | null | undefined
): PermissionCode[] => unique(normalizeCodes(userInfo?.permissions))

export const hasPermission = (
  userInfo: UserInfo | null | undefined,
  permission: PermissionCode | readonly PermissionCode[]
): boolean => {
  const userPermissions = getUserPermissions(userInfo)
  const expectedPermissions = normalizeCodes(permission)
  if (!expectedPermissions.length) return true
  return userPermissions.includes('*') || expectedPermissions.some(item => userPermissions.includes(item))
}

export const hasRole = (
  userInfo: UserInfo | null | undefined,
  role: RoleCode
): boolean => hasAnyRole(userInfo, [role])

export const hasAnyRole = (
  userInfo: UserInfo | null | undefined,
  roles: readonly RoleCode[]
): boolean => {
  const userRoles = getUserRoles(userInfo)
  const expectedRoles = normalizeCodes(roles)
  if (!expectedRoles.length) return true
  if (userRoles.includes('*')) return true
  if (SUPER_ADMIN_ROLES.some(role => userRoles.includes(role))) return true
  return expectedRoles.some(role => userRoles.includes(role))
}

export const canPerformDualControlAction = (
  userInfo: UserInfo | null | undefined,
  action: keyof typeof DUAL_CONTROL_ACTION_ROLES,
  options: AccessOptions = {}
): boolean => {
  const userRoles = getUserRoles(userInfo)
  if (!userRoles.length) return options.fallbackAllow ?? true
  return hasAnyRole(userInfo, DUAL_CONTROL_ACTION_ROLES[action])
}

const canAccessMeta = (
  meta: RouteMeta,
  userInfo: UserInfo | null | undefined,
  options: AccessOptions
): boolean => {
  if (meta.public) return true

  const expectedPermissions = normalizeCodes(meta.permission)
  const expectedAnyRoles = normalizeCodes(meta.roles ?? meta.anyRoles)
  const expectedAllRoles = normalizeCodes(meta.allRoles)

  if (!expectedPermissions.length && !expectedAnyRoles.length && !expectedAllRoles.length) {
    return true
  }

  const userPermissions = getUserPermissions(userInfo)
  const userRoles = getUserRoles(userInfo)
  const hasPermissionData = userPermissions.length > 0
  const hasRoleData = userRoles.length > 0

  if (expectedPermissions.length && hasPermissionData && hasPermission(userInfo, expectedPermissions)) {
    return true
  }

  if (expectedAnyRoles.length && hasRoleData && hasAnyRole(userInfo, expectedAnyRoles)) {
    return true
  }

  if (expectedAllRoles.length && hasRoleData) {
    const hasAll = expectedAllRoles.every(role => hasRole(userInfo, role))
    if (hasAll) return true
  }

  const hasRelevantAccessData =
    (expectedPermissions.length > 0 && hasPermissionData) ||
    ((expectedAnyRoles.length > 0 || expectedAllRoles.length > 0) && hasRoleData)

  if (!hasRelevantAccessData) {
    return options.fallbackAllow ?? true
  }

  return false
}

export const canAccessRoute = (
  target: RouteAccessTarget,
  userInfo: UserInfo | null | undefined,
  options: AccessOptions = {}
): boolean => {
  const metas = getRouteMetas(target)
  return metas.every(meta => canAccessMeta(meta, userInfo, options))
}
